/**
 * Code folding for brace blocks.
 *
 * A `StreamLanguage` has no syntax tree, so folding cannot be derived from one.
 * Instead a fold service scans forward from a line ending in `{` and counts
 * braces to find its partner. String and comment contents are skipped, because
 * a `}` inside a label would otherwise close the wrong block.
 *
 * The scan is bounded: past `MAX_SCAN_LINES` it gives up and reports no fold
 * rather than walking a pathological document on every gutter render.
 */
import { foldService } from '@codemirror/language';
import type { Extension } from '@codemirror/state';
import type { EditorState } from '@codemirror/state';

const MAX_SCAN_LINES = 5000;

export function flowScriptFolding(): Extension {
  return foldService.of((state: EditorState, lineStart: number, lineEnd: number) => {
    const text = state.doc.sliceString(lineStart, lineEnd);
    const openIndex = lastMeaningfulBrace(text);
    if (openIndex < 0) return null;

    const from = lineStart + openIndex + 1;
    const to = findMatchingBrace(state, from);
    if (to === null || to <= from) return null;
    return { from, to };
  });
}

/** Index of a trailing `{` on this line, ignoring comments and strings. */
function lastMeaningfulBrace(text: string): number {
  let inString = false;
  let index = -1;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]!;
    if (inString) {
      if (char === '\\') i += 1;
      else if (char === '"') inString = false;
      continue;
    }
    if (char === '"') {
      inString = true;
      continue;
    }
    if (char === '/' && text[i + 1] === '/') break;
    if (char === '/' && text[i + 1] === '*') {
      const close = text.indexOf('*/', i + 2);
      if (close < 0) break;
      i = close + 1;
      continue;
    }
    if (char === '{') index = i;
    else if (char === '}') index = -1;
  }
  return index;
}

function findMatchingBrace(state: EditorState, from: number): number | null {
  let depth = 1;
  let inString = false;
  let inBlockComment = false;
  const doc = state.doc;
  const startLine = doc.lineAt(from).number;
  const lastLine = Math.min(doc.lines, startLine + MAX_SCAN_LINES);

  for (let lineNumber = startLine; lineNumber <= lastLine; lineNumber += 1) {
    const line = doc.line(lineNumber);
    const text = line.text;
    const begin = lineNumber === startLine ? from - line.from : 0;

    for (let i = begin; i < text.length; i += 1) {
      const char = text[i]!;

      if (inBlockComment) {
        if (char === '*' && text[i + 1] === '/') {
          inBlockComment = false;
          i += 1;
        }
        continue;
      }
      if (inString) {
        if (char === '\\') i += 1;
        else if (char === '"') inString = false;
        continue;
      }
      if (char === '"') {
        inString = true;
        continue;
      }
      if (char === '/' && text[i + 1] === '/') break;
      if (char === '/' && text[i + 1] === '*') {
        inBlockComment = true;
        i += 1;
        continue;
      }
      if (char === '{') depth += 1;
      else if (char === '}') {
        depth -= 1;
        if (depth === 0) return line.from + i;
      }
    }
  }

  return null;
}
